// safety/safety-walks/pages/SafetyObservationsPage.tsx
// My Tasks — Responsible Person view (Miro: "Responsible Person View (No Access
// to Safety Module)"). Only observations assigned to the current user, with
// KPI cards (Assigned to Me / Open / In Progress / Overdue), filter bar and
// overdue banner. Route guarded by safety_my_tasks; server enforces
// personResponsibleId = current user.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, TriangleAlert, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../../../constants/permissions.constants';
import AccessRestricted from '../../../../components/AccessRestricted';
import { getMyTasks } from '../../../../utils/safety-walks-api';
import { StatusPill, RiskBadge, DueIndicator, fmtDate } from '../components/shared';
import type { SafetyObservation } from '../../../../types/safety-walk.types';

const FILTERS: ('All' | 'Open' | 'In Progress' | 'Closed')[] = ['All', 'Open', 'In Progress', 'Closed'];
const PAGE_SIZE = 10;

export const SafetyObservationsPage: React.FC = () => {
    const navigate = useNavigate();
    const ability = useAbility(AbilityContext);
    const [rows, setRows] = useState<SafetyObservation[]>([]);
    const [total, setTotal] = useState(0);
    const [kpis, setKpis] = useState({ assigned: 0, open: 0, inProgress: 0, overdue: 0 });
    const [filter, setFilter] = useState<'All' | 'Open' | 'In Progress' | 'Closed'>('All');
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const canView =
        ability.can(ACTIONS.READ, RESOURCES.SAFETY_MY_TASKS) ||
        ability.can(ACTIONS.READ, RESOURCES.SAFETY_OBSERVATION);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => setPage(1), [filter, debouncedSearch]);

    const fetchTasks = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getMyTasks({
                status: filter === 'All' ? undefined : filter,
                search: debouncedSearch || undefined,
                page,
                limit: PAGE_SIZE,
                sortBy: 'dueDate',
                sortDir: 'asc',
            });
            setRows(res.rows || []);
            setTotal(res.total || 0);
            setKpis(res.kpis || { assigned: 0, open: 0, inProgress: 0, overdue: 0 });
        } catch {
            // api util toasts
        } finally {
            setLoading(false);
        }
    }, [filter, debouncedSearch, page]);

    useEffect(() => {
        if (canView) fetchTasks();
    }, [fetchTasks, canView]);

    if (!canView) {
        return <AccessRestricted accessMessage="You don't have permission to access Safety Observations." />;
    }

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const kpiCards = useMemo(
        () => [
            { label: 'Assigned to Me', value: kpis.assigned, accent: 'text-blue-600' },
            { label: 'Open', value: kpis.open, accent: 'text-amber-600' },
            { label: 'In Progress', value: kpis.inProgress, accent: 'text-violet-600' },
            { label: 'Overdue', value: kpis.overdue, accent: 'text-red-600' },
        ],
        [kpis]
    );

    return (
        <div className="pb-10 px-0 sm:px-4 lg:px-0 max-w-[1400px] mx-auto w-full">
            <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
                <div>
                    <h1 className="text-2xl font-medium">Safety Observations</h1>
                    <p className="text-sm text-muted-foreground mt-1">Observations assigned to you requiring action</p>
                </div>
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {kpiCards.map((k) => (
                    <div key={k.label} className="rounded-xl border border-border bg-card p-4">
                        <div className={`text-2xl font-semibold ${k.accent}`}>{k.value}</div>
                        <div className="text-xs text-muted-foreground mt-1">{k.label}</div>
                    </div>
                ))}
            </div>

            {/* Overdue banner */}
            {kpis.overdue > 0 && (
                <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 mb-6">
                    <TriangleAlert size={18} className="text-red-700 shrink-0" />
                    <span className="text-sm text-red-700">
                        You have <strong>{kpis.overdue}</strong> overdue observation(s) — please action immediately.
                    </span>
                </div>
            )}

            {/* Filter bar */}
            <div className="flex items-center gap-3 flex-wrap mb-5">
                <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search observations..."
                        className="w-64 h-10 pl-9 pr-3 rounded-lg border border-border bg-card text-sm"
                    />
                </div>
                <div className="flex items-center gap-1 border border-border rounded-lg p-1 bg-card">
                    {FILTERS.map((f) => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`h-8 px-3 rounded-md text-xs font-medium transition-colors ${
                                filter === f ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                            }`}
                        >
                            {f}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 size={24} className="animate-spin text-muted-foreground" />
                </div>
            ) : rows.length === 0 ? (
                <div className="rounded-xl border border-border bg-card py-16 text-center text-muted-foreground">
                    No observations assigned to you
                </div>
            ) : (
                <>
                    <div className="space-y-4">
                        {rows.map((o) => (
                            <button
                                key={o.id}
                                onClick={() => navigate(`/safety/observations/${o.id}`)}
                                className="w-full text-left rounded-xl border border-border bg-card p-5 hover:bg-accent/40"
                            >
                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-semibold text-primary">{o.obsNumber}</span>
                                        <RiskBadge risk={o.riskRating} />
                                        {o.category && <span className="text-xs text-muted-foreground">{o.category}</span>}
                                        <DueIndicator isOverdue={o.isOverdue} dueDate={o.dueDate} />
                                    </div>
                                    <StatusPill status={o.status} />
                                </div>
                                <p className="text-sm font-medium mt-2">{o.title || 'Untitled observation'}</p>
                                {o.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{o.description}</p>}
                                <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground flex-wrap">
                                    {o.walk && (
                                        <>
                                            <span>Walk: {o.walk.walkNumber}</span>
                                            <span>Project: {o.walk.project}</span>
                                        </>
                                    )}
                                    {o.dueDate && <span>Due: {fmtDate(o.dueDate)}</span>}
                                </div>
                            </button>
                        ))}
                    </div>

                    <div className="flex items-center justify-between mt-5 text-sm text-muted-foreground">
                        <span>{total} observation(s)</span>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page <= 1}
                                className="h-9 w-9 rounded-lg border border-border bg-card flex items-center justify-center hover:bg-accent disabled:opacity-40"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <span>{page} / {totalPages}</span>
                            <button
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                disabled={page >= totalPages}
                                className="h-9 w-9 rounded-lg border border-border bg-card flex items-center justify-center hover:bg-accent disabled:opacity-40"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
